import { useState, useMemo } from 'react';
import { useList } from '@refinedev/core';
import { Button } from '@/components/ui/button';
import { ColorPicker } from '@/components/ui/color-picker';
import { Input } from '@/components/ui/input';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  FormField,
  MultiCurrencyPrices,
  type MultiCurrencyPricesValue,
} from '@/components/forms';
import {
  LanguageSwitcher,
  TranslationsForm,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CreditCard, Palette, Users, Tag } from 'lucide-react';
import { MultiSelect } from '@/components/ui/multi-select';
import { notify } from '@/lib/notify';
import {
  DEFAULT_TIER_CARD_APPEARANCE,
  type PricingTierCardAppearance,
  type PricingTierCardThemeColors,
  type PricingTierDraft,
  createEmptyTierDraft,
} from './types';
import { MembershipCardPreview } from './MembershipCardPreview';

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

const TIER_TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  {
    name: 'name',
    label: 'Название тарифа',
    type: 'input',
  },
];

const GRADIENT_PRESETS = [
  { name: 'Золотой', from: '#FCA311', to: '#FFD99A', angle: 264 },
  { name: 'Синий', from: '#375086', to: '#A1BFFF', angle: 183 },
  { name: 'Зелёный', from: '#2D6A4F', to: '#95D5B2', angle: 180 },
  { name: 'Фиолетовый', from: '#5A189A', to: '#C77DFF', angle: 180 },
  { name: 'Красный', from: '#9D0208', to: '#F48C06', angle: 180 },
] as const;

interface PricingTierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: PricingTierDraft | null;
  order: number;
  onSave: (tier: PricingTierDraft) => void;
  /** Показывать ли секцию семейного тарифа (только для абонементов) */
  showFamily?: boolean;
}

/**
 * Конвертирует PricingTierDraft.translations[] → TranslationData (Record<lang, Record<field, value>>)
 */
function draftToTranslationData(translations: PricingTierDraft['translations']): TranslationData {
  const data: TranslationData = {};
  for (const t of translations) {
    data[t.languageCode] = { name: t.name };
  }
  return data;
}

/**
 * Конвертирует TranslationData → PricingTierDraft.translations[]
 */
function translationDataToDraft(
  data: TranslationData,
): PricingTierDraft['translations'] {
  return Object.entries(data).map(([code, fields]) => ({
    languageCode: code,
    name: fields.name ?? '',
    shortDescription: null,
  }));
}

/**
 * Конвертирует PricingTierDraft цены → MultiCurrencyPricesValue
 */
function draftToMultiCurrencyValue(draft: PricingTierDraft): MultiCurrencyPricesValue {
  const basePrice = draft.prices[draft.baseCurrency] ?? null;
  return {
    price: basePrice,
    currency: draft.baseCurrency,
    prices: Object.keys(draft.prices).length > 0 ? draft.prices : null,
    baseCurrency: draft.baseCurrency,
  };
}

/**
 * Sheet-форма создания/редактирования тарифа (offline mode).
 * Работает полностью в памяти, без API-вызовов для сохранения.
 * Дизайн повторяет TierModal из PricingTiersSection.
 */
export function PricingTierDialog({
  open,
  onOpenChange,
  tier,
  order,
  onSave,
  showFamily = false,
}: PricingTierDialogProps) {
  const isEditing = tier !== null;

  // ─── State ───────────────────────────────────────────────
  const [draft, setDraft] = useState<PricingTierDraft>(
    () => tier ?? createEmptyTierDraft(order),
  );
  const [activeLanguage, setActiveLanguage] = useState<string>('ru');
  const [appearanceTheme, setAppearanceTheme] = useState<'light' | 'dark'>('light');
  const [previewOrientation, setPreviewOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

  // ─── Languages ───────────────────────────────────────────
  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });

  const languages: TranslationLanguage[] = useMemo(
    () =>
      (langResult?.data ?? []).map((l) => ({
        code: l.code,
        name: l.name,
        nativeName: l.nativeName,
        flag: l.flag ?? l.flagEmoji ?? '🏳️',
      })),
    [langResult?.data],
  );

  // ─── Derived state ──────────────────────────────────────
  const translations = useMemo(
    () => draftToTranslationData(draft.translations),
    [draft.translations],
  );

  const pricing = useMemo(
    () => draftToMultiCurrencyValue(draft),
    [draft],
  );

  const hasPrice = pricing.price !== null && pricing.price > 0;

  // ─── Handlers ────────────────────────────────────────────
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraft(tier ?? createEmptyTierDraft(order));
      setActiveLanguage('ru');
    }
    onOpenChange(nextOpen);
  };

  const handleTranslationsChange = (data: TranslationData) => {
    setDraft((prev) => ({
      ...prev,
      translations: translationDataToDraft(data),
    }));
  };

  const handlePricingChange = (value: MultiCurrencyPricesValue) => {
    setDraft((prev) => ({
      ...prev,
      prices: value.prices ?? {},
      baseCurrency: value.baseCurrency,
    }));
  };

  const handlePromoPricingChange = (value: MultiCurrencyPricesValue) => {
    setDraft((prev) => ({
      ...prev,
      promoPrices: value.prices && Object.keys(value.prices).length > 0 ? value.prices : null,
    }));
  };

  const promoPricing = useMemo((): MultiCurrencyPricesValue => {
    const promoBase = draft.promoPrices?.[draft.baseCurrency] ?? null;
    return {
      price: promoBase,
      currency: draft.baseCurrency,
      prices: draft.promoPrices && Object.keys(draft.promoPrices).length > 0 ? draft.promoPrices : null,
      baseCurrency: draft.baseCurrency,
    };
  }, [draft.promoPrices, draft.baseCurrency]);

  const hasPromoEnabled = draft.promoPrices !== null || draft.promoUntil !== null;

  const languageOptions = useMemo(
    () =>
      languages.map((l) => ({
        value: l.code,
        label: `${l.flag ?? ''} ${l.nativeName} (${l.code.toUpperCase()})`.trim(),
      })),
    [languages],
  );

  const handleSave = () => {
    // Валидация: для каждого targetLanguage должен быть перевод name
    if (draft.targetLanguages.length > 0) {
      const missing: string[] = [];
      for (const code of draft.targetLanguages) {
        const tr = draft.translations.find((t) => t.languageCode === code);
        if (!tr || !tr.name.trim()) {
          missing.push(code.toUpperCase());
        }
      }
      if (missing.length > 0) {
        notify.error(
          `Заполните название тарифа для языков: ${missing.join(', ')}`,
        );
        return;
      }
    }
    onSave(draft);
    onOpenChange(false);
  };

  const updateField = <K extends keyof PricingTierDraft>(
    field: K,
    value: PricingTierDraft[K],
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const effectiveAppearance: PricingTierCardAppearance =
    draft.cardAppearance ?? DEFAULT_TIER_CARD_APPEARANCE;

  const updateAppearanceColor = (
    theme: 'light' | 'dark',
    key: keyof PricingTierCardThemeColors,
    value: string,
  ) => {
    setDraft((prev) => {
      const base = prev.cardAppearance ?? DEFAULT_TIER_CARD_APPEARANCE;
      const nextAppearance: PricingTierCardAppearance = {
        light: { ...base.light },
        dark: { ...base.dark },
      };
      nextAppearance[theme] = { ...nextAppearance[theme], [key]: value };
      return { ...prev, cardAppearance: nextAppearance };
    });
  };

  const resetAppearance = () => {
    updateField('cardAppearance', null);
  };

  const previewTierName = useMemo(() => {
    const ru = draft.translations.find((tr) => tr.languageCode === 'ru');
    return (ru?.name && ru.name.trim()) || 'Разовый абонемент';
  }, [draft.translations]);

  const previewExpiresAt = useMemo(() => {
    if (!draft.durationDays) { return null; }
    const date = new Date();
    date.setDate(date.getDate() + draft.durationDays);
    return date.toISOString();
  }, [draft.durationDays]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-2">
          <div className="flex items-center justify-between pr-6">
            <SheetTitle>{isEditing ? 'Редактировать тариф' : 'Новый тариф'}</SheetTitle>
            <div className="flex items-center gap-2">
              <Select
                value={draft.isActive ? 'active' : 'inactive'}
                onValueChange={(v) => updateField('isActive', v === 'active')}
              >
                <SelectTrigger className="h-8 w-[130px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Активен</SelectItem>
                  <SelectItem value="inactive">Неактивен</SelectItem>
                </SelectContent>
              </Select>
              {languages.length > 0 && (
                <LanguageSwitcher
                  languages={languages}
                  activeLanguage={activeLanguage}
                  onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
                  fields={TIER_TRANSLATION_FIELDS}
                  value={translations}
                  variant="compact"
                />
              )}
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-4">
          {/* Translations (name) */}
          {languages.length > 0 && (
            <TranslationsForm
              fields={TIER_TRANSLATION_FIELDS}
              value={translations}
              onChange={handleTranslationsChange}
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
            />
          )}

          {/* Duration */}
          <FormField
            label="Срок действия (дней)"
            name="tierDurationDays"
            hint="Оставьте пустым для бессрочного доступа"
          >
            <Input
              type="number"
              value={draft.durationDays ?? ''}
              onChange={(e) => {
                const val = e.target.value === '' ? null : Number(e.target.value);
                updateField('durationDays', val);
              }}
              placeholder="30"
            />
          </FormField>

          <FormField
            label="Видимость по языкам аудитории"
            name="targetLanguages"
            hint="Пустое = виден всем. При выборе языков обязательны переводы названия для каждого выбранного языка."
          >
            <MultiSelect
              options={languageOptions}
              value={draft.targetLanguages}
              onChange={(value) => updateField('targetLanguages', value)}
              placeholder="Виден всем"
              searchPlaceholder="Поиск языка..."
              emptyMessage="Языки не найдены"
            />
          </FormField>

          {/* Multi-currency pricing */}
          <MultiCurrencyPrices
            value={pricing}
            onChange={handlePricingChange}
            label="Ценообразование"
            required
          />

          <Separator />

          {/* Promo pricing */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Промо-цена</span>
              <span className="text-xs text-muted-foreground">(опционально)</span>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="promoEnabled"
                checked={hasPromoEnabled}
                onCheckedChange={(checked) => {
                  if (checked) {
                    updateField('promoPrices', {});
                    updateField('promoUntil', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString());
                  } else {
                    updateField('promoPrices', null);
                    updateField('promoUntil', null);
                  }
                }}
              />
              <label htmlFor="promoEnabled" className="text-sm font-medium leading-none">
                Включить промо-цену
              </label>
            </div>

            {hasPromoEnabled && (
              <>
                <MultiCurrencyPrices
                  value={promoPricing}
                  onChange={handlePromoPricingChange}
                  label="Промо-цена (акционная)"
                />

                <FormField label="Акция действует до" name="promoUntil">
                  <DateTimePicker
                    value={draft.promoUntil ? new Date(draft.promoUntil) : null}
                    onChange={(date) =>
                      updateField('promoUntil', date ? date.toISOString() : null)
                    }
                    placeholder="Не задано"
                  />
                </FormField>
              </>
            )}
          </div>

          <Separator />

          {/* Family tier (only for subscriptions) */}
          {showFamily && (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="tierIsFamily"
                  checked={draft.isFamily}
                  onCheckedChange={(checked) => updateField('isFamily', checked === true)}
                />
                <label htmlFor="tierIsFamily" className="text-sm font-medium leading-none flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Семейный тариф
                </label>
              </div>
              {draft.isFamily && (
                <FormField label="Количество мест в семье" name="tierFamilySlots">
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={draft.familySlots}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (!Number.isNaN(val) && val >= 1) {
                        updateField('familySlots', val);
                      }
                    }}
                    placeholder="5"
                  />
                </FormField>
              )}
            </div>
          )}

          {/* Legacy «Оформление» (gradient + image) — только для не-абонементов
              (каналы/курсы/книги). У абонементов цвета карточки настраиваются
              в отдельной секции «Карточка абонемента» ниже (cardAppearance),
              а image/gradient здесь не нужны и только путают. */}
          {!showFamily && (
          <>
          <Separator />

          {/* Design / Gradient */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Оформление</span>
              <span className="text-xs text-muted-foreground">(опционально)</span>
            </div>

            {/* Gradient presets */}
            <div className="flex flex-wrap gap-1.5">
              {GRADIENT_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs hover:bg-accent transition-colors"
                  onClick={() => {
                    updateField('gradientFrom', preset.from);
                    updateField('gradientTo', preset.to);
                    updateField('gradientAngle', preset.angle);
                  }}
                >
                  <div
                    className="h-3 w-3 rounded-full shrink-0 border"
                    style={{
                      background: `linear-gradient(${preset.angle}deg, ${preset.from}, ${preset.to})`,
                    }}
                  />
                  {preset.name}
                </button>
              ))}
              {(draft.gradientFrom || draft.gradientTo) && (
                <button
                  type="button"
                  className="rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-accent transition-colors"
                  onClick={() => {
                    updateField('gradientFrom', null);
                    updateField('gradientTo', null);
                    updateField('gradientAngle', 180);
                  }}
                >
                  Сбросить
                </button>
              )}
            </div>

            {/* Color pickers */}
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Цвет от" name="gradientFrom">
                <ColorPicker
                  value={draft.gradientFrom ?? ''}
                  onChange={(color) => updateField('gradientFrom', color)}
                  fallbackColor="#FCA311"
                  placeholder="#FCA311"
                />
              </FormField>

              <FormField label="Цвет до" name="gradientTo">
                <ColorPicker
                  value={draft.gradientTo ?? ''}
                  onChange={(color) => updateField('gradientTo', color)}
                  fallbackColor="#FFD99A"
                  placeholder="#FFD99A"
                />
              </FormField>

              <FormField label="Угол" name="gradientAngle">
                <Input
                  type="number"
                  value={draft.gradientAngle}
                  onChange={(e) => updateField('gradientAngle', Number(e.target.value))}
                  min={0}
                  max={360}
                  placeholder="180"
                />
              </FormField>
            </div>

            {/* Gradient preview */}
            {draft.gradientFrom && draft.gradientTo && (
              <div
                className="h-10 w-full rounded-md border"
                style={{
                  background: `linear-gradient(${draft.gradientAngle || 180}deg, ${draft.gradientFrom}, ${draft.gradientTo})`,
                }}
              />
            )}
          </div>
          </>
          )}

          {showFamily && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Карточка абонемента</span>
                    <span className="text-xs text-muted-foreground">(опционально)</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetAppearance}
                    disabled={draft.cardAppearance === null}
                  >
                    Сбросить к дефолтам
                  </Button>
                </div>

                <Tabs
                  value={appearanceTheme}
                  onValueChange={(value) => {
                    if (value === 'light' || value === 'dark') {
                      setAppearanceTheme(value);
                    }
                  }}
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="light">Светлая тема</TabsTrigger>
                    <TabsTrigger value="dark">Тёмная тема</TabsTrigger>
                  </TabsList>

                  <TabsContent value="light" className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <FormField label="Фон" name="appearanceLightBg">
                        <ColorPicker
                          value={effectiveAppearance.light.bg}
                          onChange={(color) => updateAppearanceColor('light', 'bg', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.bg}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.bg}
                        />
                      </FormField>
                      <FormField label="Паттерн (основной)" name="appearanceLightPP">
                        <ColorPicker
                          value={effectiveAppearance.light.patternPrimary}
                          onChange={(color) => updateAppearanceColor('light', 'patternPrimary', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.patternPrimary}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.patternPrimary}
                        />
                      </FormField>
                      <FormField label="Паттерн (вторичный)" name="appearanceLightPS">
                        <ColorPicker
                          value={effectiveAppearance.light.patternSecondary}
                          onChange={(color) => updateAppearanceColor('light', 'patternSecondary', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.light.patternSecondary}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.light.patternSecondary}
                        />
                      </FormField>
                    </div>
                  </TabsContent>

                  <TabsContent value="dark" className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <FormField label="Фон" name="appearanceDarkBg">
                        <ColorPicker
                          value={effectiveAppearance.dark.bg}
                          onChange={(color) => updateAppearanceColor('dark', 'bg', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.bg}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.bg}
                        />
                      </FormField>
                      <FormField label="Паттерн (основной)" name="appearanceDarkPP">
                        <ColorPicker
                          value={effectiveAppearance.dark.patternPrimary}
                          onChange={(color) => updateAppearanceColor('dark', 'patternPrimary', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.patternPrimary}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.patternPrimary}
                        />
                      </FormField>
                      <FormField label="Паттерн (вторичный)" name="appearanceDarkPS">
                        <ColorPicker
                          value={effectiveAppearance.dark.patternSecondary}
                          onChange={(color) => updateAppearanceColor('dark', 'patternSecondary', color)}
                          fallbackColor={DEFAULT_TIER_CARD_APPEARANCE.dark.patternSecondary}
                          placeholder={DEFAULT_TIER_CARD_APPEARANCE.dark.patternSecondary}
                        />
                      </FormField>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Превью</span>
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      variant={previewOrientation === 'horizontal' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewOrientation('horizontal')}
                    >
                      Горизонтальная
                    </Button>
                    <Button
                      type="button"
                      variant={previewOrientation === 'vertical' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPreviewOrientation('vertical')}
                    >
                      Вертикальная
                    </Button>
                  </div>
                </div>

                <div className="flex justify-center overflow-x-auto rounded-md border bg-muted/30 p-4">
                  <MembershipCardPreview
                    appearance={draft.cardAppearance}
                    theme={appearanceTheme}
                    tierName={previewTierName}
                    userName="DALER MUSTAFAEV"
                    expiresAt={previewExpiresAt}
                    isFamily={draft.isFamily}
                    labels={{
                      validUntil: 'Действует до',
                      validity: 'Срок действия:',
                      lifetime: 'Бессрочно',
                      family: 'Семейный',
                    }}
                    orientation={previewOrientation}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-end gap-2 p-4">
          <Button variant="outline" size="default" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button size="default" onClick={handleSave} disabled={!hasPrice}>
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
